/**
 * Helper de notificação sonora via Web Audio API (D-96, 2026-05-14).
 *
 * Sem dependência de arquivo de áudio — gera um beep curto inline.
 * 2 notas (A5 → E6), volume baixo (0.18), duração ~400ms — semelhante
 * a notif iOS. Autoplay pós-interação do usuário (login conta como
 * interação, então sempre funciona quando o admin já está logado).
 *
 * Mute persistido em localStorage. Toggle por playNotif(false) NÃO está aqui;
 * mute é via `setNotifMuted(true)`. O som NÃO toca quando muted.
 */

const MUTE_KEY = 'myros_notif_muted'
const BROWSER_NOTIF_KEY = 'myros_browser_notif_enabled'

let ctx: AudioContext | null = null

export function isNotifMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1'
  } catch {
    return false
  }
}

export function setNotifMuted(muted: boolean): void {
  try {
    if (muted) localStorage.setItem(MUTE_KEY, '1')
    else localStorage.removeItem(MUTE_KEY)
  } catch {
    /* storage indisponível — silent */
  }
}

// ─── Browser Notification API (D-96 / iteração 2) ─────────────────────
// Notificação nativa do OS quando a aba está em background.
// Usuário precisa dar permissão. Mantemos opt-in: só pede permissão se o
// usuário ativou em UI; depois respeita o estado em localStorage.

export function browserNotifEnabled(): boolean {
  if (typeof window === 'undefined' || !('Notification' in window)) return false
  try {
    return localStorage.getItem(BROWSER_NOTIF_KEY) === '1' && Notification.permission === 'granted'
  } catch {
    return false
  }
}

export function browserNotifPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  return Notification.permission
}

/** Pede permissão ao usuário e, se aceito, marca como ativado em localStorage. */
export async function requestBrowserNotif(): Promise<NotificationPermission | 'unsupported'> {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  try {
    if (Notification.permission === 'granted') {
      localStorage.setItem(BROWSER_NOTIF_KEY, '1')
      return 'granted'
    }
    if (Notification.permission === 'denied') return 'denied'
    const result = await Notification.requestPermission()
    if (result === 'granted') localStorage.setItem(BROWSER_NOTIF_KEY, '1')
    return result
  } catch {
    return 'denied'
  }
}

export function disableBrowserNotif(): void {
  try { localStorage.removeItem(BROWSER_NOTIF_KEY) } catch { /* ignore */ }
}

/** Dispara uma notificação nativa do OS. Silenciosa se não ativado/sem permissão. */
export function showBrowserNotif(title: string, body: string, onClick?: () => void): void {
  if (!browserNotifEnabled()) return
  try {
    const n = new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: 'myros-novo-pedido', // dedup: nova notif substitui a anterior do mesmo tag
      silent: false,
    })
    if (onClick) {
      n.onclick = () => {
        try { window.focus() } catch { /* ignore */ }
        onClick()
        n.close()
      }
    }
    // Auto-fecha em 8s (browser pode ignorar em alguns OSs)
    setTimeout(() => { try { n.close() } catch { /* ignore */ } }, 8_000)
  } catch {
    /* silent fallback */
  }
}

export function playNotif(): void {
  if (isNotifMuted()) return
  try {
    if (!ctx) {
      const AC = window.AudioContext || (window as any).webkitAudioContext
      if (!AC) return
      ctx = new AC()
    }
    // Resume se foi suspenso (auto suspend do Chrome em tab background)
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => { /* ignore */ })
    }
    const t = ctx.currentTime
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.type = 'sine'
    osc.frequency.setValueAtTime(880, t)         // A5
    osc.frequency.setValueAtTime(1320, t + 0.12) // E6
    gain.gain.setValueAtTime(0.18, t)
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4)
    osc.start(t)
    osc.stop(t + 0.4)
  } catch {
    /* silent fallback */
  }
}
